/**
 * The pages a Firm API list came through: each list answers the cursor of
 * its next page, and the page remembers the cursors it came through to go
 * back. The cursors are the firm's they were read in: once platform staff
 * choose another firm, the list is on its first page again.
 */

import { createSignal } from "solid-js";

import { firmId } from "./firm";

export type CursorPages<C> = ReturnType<typeof createCursorPages<C>>;

export function createCursorPages<C>() {
  const [trail, setTrail] = createSignal<{ firm: string | null; cursors: C[] }>({
    firm: firmId(),
    cursors: [],
  });
  const cursors = (): C[] => (trail().firm === firmId() ? trail().cursors : []);
  const walk = (next: C[]) => setTrail({ firm: firmId(), cursors: next });
  return {
    /** The cursor of the page shown; `undefined` is the first page. */
    current: (): C | undefined => cursors().at(-1),
    page: () => cursors().length + 1,
    next: (cursor: C) => walk([...cursors(), cursor]),
    previous: () => walk(cursors().slice(0, -1)),
    reset: () => walk([]),
  };
}
